// The alarm ledger: the list (open by default), and one alarm with its timeline, notifications and
// corrective actions. Acknowledging needs a comment and a corrective action (HACCP), optionally a photo.

import type { AlarmDetail as Detail, CorrectiveActionInput } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { AlarmStateBadge, SeverityBadge } from '../components/domain.tsx';
import { useCurrentOrg } from '../components/Shell.tsx';
import {
  Alert, Badge, Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner, Table, Td, Textarea,
} from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { ApiError, uploadFile } from '../lib/api.ts';
import { formatDateTime, formatDuration, formatTemp } from '../lib/format.ts';
import { keys, orgApi, refreshScoped, useAlarm, useAlarmPages, useAssets, useMembers, useSites } from '../lib/queries.ts';

const CATEGORIES = ['door_closed', 'moved_products', 'product_discarded', 'technician_called', 'probe_displaced',
  'false_alarm', 'other'] as const;

export function Alarms() {
  const { orgId } = useCurrentOrg();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const open = params.get('open') !== 'false';
  const severity = params.get('severity') ?? '';
  const site = params.get('site_id') ?? '';
  const filter = [`open=${open}`, severity && `severity=${severity}`, site && `site_id=${site}`].filter(Boolean).join('&');
  // Refreshed every 30 s and on live updates; further pages are loaded on demand.
  const list = useAlarmPages(orgId, filter);
  const assets = useAssets(orgId);
  const sites = useSites(orgId);
  const assetName = useMemo(() => new Map((assets.data?.data ?? []).map((a) => [a.id, a.name])), [assets.data]);
  const siteTz = useMemo(() => new Map((sites.data?.data ?? []).map((s) => [s.id, s.timezone])), [sites.data]);
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];

  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  return (
    <div>
      <PageHeader title={t('alarms.title')} actions={(
        <>
          <Select aria-label={t('alarms.show')} className="w-auto" value={String(open)} onChange={(e) => set('open', e.target.value === 'true' ? '' : 'false')}>
            <option value="true">{t('alarms.open')}</option>
            <option value="false">{t('alarms.closed')}</option>
          </Select>
          <Select aria-label={t('alarms.severity')} className="w-auto" value={severity} onChange={(e) => set('severity', e.target.value)}>
            <option value="">{t('alarms.allSeverities')}</option>
            <option value="critical">{t('severity.critical')}</option>
            <option value="warning">{t('severity.warning')}</option>
          </Select>
          {(sites.data?.data.length ?? 0) > 1 && (
            <Select aria-label={t('overview.site')} className="w-auto" value={site} onChange={(e) => set('site_id', e.target.value)}>
              <option value="">{t('overview.allSites')}</option>
              {sites.data!.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          )}
        </>
      )} />
      {list.isPending && <Spinner />}
      {list.data && rows.length === 0 && <EmptyState title={open ? t('alarms.noneOpen') : t('alarms.none')} />}
      {rows.length > 0 && (
        <Card>
          <Table head={[t('alarms.alarm'), t('alarms.asset'), t('alarms.since'), t('alarms.value'), t('alarms.state')]}>
            {rows.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <Td>
                  <Link to={a.id} className="flex items-center gap-2 font-medium text-brand-700 hover:underline">
                    <SeverityBadge severity={a.severity} /> {t(`alarmType.${a.type}`)}
                  </Link>
                  {a.retrospective && <span className="text-xs text-slate-500">{t('alarms.retrospective')}</span>}
                </Td>
                <Td>{a.asset_id ? assetName.get(a.asset_id) ?? '—' : t('alarms.deviceLevel')}</Td>
                <Td>{formatDateTime(a.condition_since, lang, a.site_id ? siteTz.get(a.site_id) : undefined)}
                  {a.cleared_at && <span className="block text-xs text-slate-500">
                    {t('alarms.lasted', { duration: formatDuration((Date.parse(a.cleared_at) - Date.parse(a.condition_since)) / 1000, lang) })}
                  </span>}
                </Td>
                <Td className="tabular-nums">{a.peak_value_c !== null ? formatTemp(a.peak_value_c, lang)
                  : a.trigger_value_c !== null ? formatTemp(a.trigger_value_c, lang) : '—'}</Td>
                <Td><AlarmStateBadge alarm={a} /></Td>
              </tr>
            ))}
          </Table>
          {list.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>{t('common.more')}</Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one alarm

export function AlarmDetail() {
  const { alarmId = '' } = useParams();
  const { orgId, can } = useCurrentOrg();
  const { t, lang } = useI18n();
  const alarm = useAlarm(orgId, alarmId);
  const assets = useAssets(orgId);
  const sites = useSites(orgId);
  const members = useMembers(orgId);
  if (alarm.isPending) return <Spinner />;
  if (alarm.isError) {
    return <Alert tone="red">{alarm.error instanceof ApiError && alarm.error.status === 404 ? t('alarms.notFound') : t('errors.unavailable')}</Alert>;
  }
  const a = alarm.data;
  const asset = assets.data?.data.find((x) => x.id === a.asset_id);
  const tz = sites.data?.data.find((s) => s.id === a.site_id)?.timezone;
  const who = (id: string | null) => members.data?.data.find((m) => m.user_id === id)?.display_name ?? '—';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={<span className="flex flex-wrap items-center gap-3">{t(`alarmType.${a.type}`)} <SeverityBadge severity={a.severity} />
          <AlarmStateBadge alarm={a} /></span>}
        subtitle={asset ? <Link to={`../assets/${asset.id}`} className="text-brand-700 hover:underline">{asset.name}</Link> : t('alarms.deviceLevel')} />

      {a.retrospective && <Alert tone="amber">{t('alarms.retrospectiveHint')}</Alert>}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card title={t('alarms.summary')} className="lg:col-span-1">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <dt className="text-slate-500">{t('alarms.conditionSince')}</dt><dd>{formatDateTime(a.condition_since, lang, tz, true)}</dd>
            <dt className="text-slate-500">{t('alarms.triggeredAt')}</dt><dd>{formatDateTime(a.triggered_at, lang, tz, true)}</dd>
            <dt className="text-slate-500">{t('alarms.clearedAt')}</dt><dd>{formatDateTime(a.cleared_at, lang, tz, true)}</dd>
            <dt className="text-slate-500">{t('alarms.triggerValue')}</dt><dd>{formatTemp(a.trigger_value_c, lang)}</dd>
            <dt className="text-slate-500">{t('alarms.peak')}</dt><dd>{formatTemp(a.peak_value_c, lang)}</dd>
            <dt className="text-slate-500">{t('alarms.level')}</dt><dd>{a.current_level}</dd>
            <dt className="text-slate-500">{t('alarms.acknowledgedBy')}</dt>
            <dd>{a.acknowledged_at ? `${who(a.acknowledged_by)}, ${formatDateTime(a.acknowledged_at, lang, tz)}` : '—'}</dd>
            {a.rule && (
              <>
                <dt className="text-slate-500">{t('alarms.rule')}</dt>
                <dd>{'threshold_c' in a.rule.params ? t(a.type === 'low' ? 'assets.thresholdLow' : 'assets.thresholdHigh',
                  { value: formatTemp(a.rule.params.threshold_c, lang) }) : t(`alarmType.${a.type}`)} (v{a.rule_version})</dd>
              </>
            )}
          </dl>
        </Card>

        <div className="flex flex-col gap-5 lg:col-span-2">
          {!a.acknowledged_at && can('operator') && <Acknowledge alarm={a} />}
          {a.acknowledged_at && can('operator') && <AddAction alarm={a} />}
          <Card title={t('alarms.actions')}>
            {a.corrective_actions.length === 0 ? <p className="text-sm text-slate-500">{t('alarms.noActions')}</p> : (
              <ul className="divide-y divide-slate-100">
                {a.corrective_actions.map((c) => (
                  <li key={c.id} className="py-2 text-sm">
                    <p className="text-xs text-slate-500">{formatDateTime(c.created_at, lang, tz)} · {who(c.created_by)} ·
                      {' '}{t(`correctiveCategory.${c.category}`)}</p>
                    <p className="whitespace-pre-line text-slate-800">{c.comment}</p>
                    {c.product_disposition && <p className="text-slate-600">{t('alarms.dispositionValue', { value: c.product_disposition })}</p>}
                    {c.photo_url && <a href={c.photo_url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                      {t('alarms.photo')}</a>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title={t('alarms.timeline')}>
          <ol className="relative ml-2 border-l border-slate-200">
            {a.events.map((e) => (
              <li key={e.id} className="mb-3 ml-4">
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-slate-400" />
                <p className="text-sm font-medium text-slate-800">{t(`alarmEvent.${e.kind}`)}</p>
                <p className="text-xs text-slate-500">{formatDateTime(e.at, lang, tz, true)}{e.actor_id ? ` · ${who(e.actor_id)}` : ''}</p>
              </li>
            ))}
          </ol>
        </Card>
        <Card title={t('alarms.notifications')}>
          {a.notifications.length === 0 ? <p className="text-sm text-slate-500">{t('alarms.noNotifications')}</p> : (
            <Table head={[t('alarms.recipient'), t('alarms.channel'), t('alarms.sent'), t('alarms.delivery')]}>
              {a.notifications.map((n) => (
                <tr key={n.id}>
                  <Td>{n.recipient_name}<span className="block text-xs text-slate-500">{t(`notificationReason.${n.reason}`)} · {t('alarms.levelN', { n: n.level })}</span></Td>
                  <Td>{t(`channel.${n.channel}`)}</Td>
                  <Td>{formatDateTime(n.created_at, lang, tz)}</Td>
                  <Td><Badge tone={n.status === 'delivered' || n.status === 'read' ? 'green' : n.status === 'failed' || n.status === 'quota_exceeded' ? 'red'
                    : n.status === 'suppressed_quiet_hours' ? 'slate' : 'blue'}>{t(`notificationStatus.${n.status}`)}</Badge></Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

function CorrectiveForm({ submitLabel, onSubmit }: { submitLabel: string; onSubmit: (b: CorrectiveActionInput) => Promise<void> }) {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const [category, setCategory] = useState<CorrectiveActionInput['category']>('door_closed');
  const [comment, setComment] = useState('');
  const [disposition, setDisposition] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const photoId = photo ? await uploadFile(orgId, photo, 'corrective_action_photo') : undefined;
      await onSubmit({ category, comment, ...(disposition ? { product_disposition: disposition } : {}),
        ...(photoId ? { photo_upload_id: photoId } : {}) });
      setComment('');
      setDisposition('');
      setPhoto(null);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'already_acknowledged' ? t('alarms.alreadyAcknowledged') : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      {error && <div className="sm:col-span-2"><Alert tone="red">{error}</Alert></div>}
      <Field label={t('alarms.category')}>
        {(p) => (
          <Select {...p} value={category} onChange={(e) => setCategory(e.target.value as CorrectiveActionInput['category'])}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{t(`correctiveCategory.${c}`)}</option>)}
          </Select>
        )}
      </Field>
      <Field label={t('alarms.disposition')} hint={t('alarms.dispositionHint')}>
        {(p) => <Input {...p} value={disposition} onChange={(e) => setDisposition(e.target.value)} maxLength={1000} />}
      </Field>
      <Field label={t('alarms.comment')} className="sm:col-span-2">
        {(p) => <Textarea {...p} value={comment} onChange={(e) => setComment(e.target.value)} required maxLength={4000} />}
      </Field>
      <Field label={t('alarms.photoOptional')} hint={t('alarms.photoHint')}>
        {(p) => <Input {...p} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />}
      </Field>
      <div className="flex items-end justify-end">
        <Button type="submit" loading={busy} disabled={!comment.trim()}>{submitLabel}</Button>
      </div>
    </form>
  );
}

function Acknowledge({ alarm }: { alarm: Detail }) {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const qc = useQueryClient();
  return (
    <Card title={t('alarms.acknowledge')}>
      <p className="mb-3 text-sm text-slate-600">{t('alarms.acknowledgeHint')}</p>
      <CorrectiveForm submitLabel={t('alarms.acknowledge')} onSubmit={async (b) => {
        const detail = await orgApi(orgId).acknowledge(alarm.id, b);
        qc.setQueryData(keys.scoped(orgId, 'alarm', alarm.id), detail);
        await refreshScoped(qc, orgId, ['alarms', 'overview']);
      }} />
    </Card>
  );
}

function AddAction({ alarm }: { alarm: Detail }) {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  if (!open) {
    return <div><Button variant="secondary" onClick={() => setOpen(true)}>{t('alarms.addAction')}</Button></div>;
  }
  return (
    <Card title={t('alarms.addAction')}>
      <CorrectiveForm submitLabel={t('common.save')} onSubmit={async (b) => {
        await orgApi(orgId).addCorrectiveAction(alarm.id, b);
        await refreshScoped(qc, orgId, ['alarm']);
        setOpen(false);
      }} />
    </Card>
  );
}
